/**
 * Use Tabs Hook
 *
 * Custom hook for fetching and managing tabs data.
 *
 * @since 1.0.0
 */

import apiFetch from '@wordpress/api-fetch';
import { useEffect, useState } from 'react';

import type { Tab } from '@/admin/components/tab-list/types';

import type { PaginationInfo, UseTabsParams, UseTabsReturn } from './types';

/**
 * Custom hook for managing tabs data
 *
 * @since 1.0.0
 *
 * @param params Hook parameters.
 *
 * @return Hook return value.
 */
export const useTabs = (params: UseTabsParams = {}): UseTabsReturn => {
  const {
    search = '',
    postsPerPage = 20,
    page: initialPage = 1,
    orderBy = 'date',
    order = 'DESC',
    includeContent = true,
    allStatuses = false,
  } = params;

  const [tabs, setTabs] = useState<Tab[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(initialPage);
  const [currentPostsPerPage, setCurrentPostsPerPage] = useState<number>(postsPerPage);

  const fetchTabs = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const queryParams = new URLSearchParams({
        search,
        posts_per_page: currentPostsPerPage.toString(),
        page: currentPage.toString(),
        orderby: orderBy,
        order,
        include_content: includeContent.toString(),
        all_statuses: allStatuses.toString(),
      });

      const apiUrl = `/redfox-tab-manager/v1/available-tabs?${queryParams.toString()}`;

      const response = await apiFetch<{
        data: Tab[];
        pagination?: {
          current_page: number;
          total_pages: number;
          total_items: number;
          items_per_page: number;
          has_next_page: boolean;
          has_prev_page: boolean;
        };
      }>({
        path: apiUrl,
      });

      if (response?.data) {
        setTabs(response.data);
        // Set pagination info
        if (response.pagination) {
          setPagination({
            currentPage: response.pagination.current_page,
            totalPages: response.pagination.total_pages,
            totalItems: response.pagination.total_items,
            itemsPerPage: response.pagination.items_per_page,
            hasNextPage: response.pagination.has_next_page,
            hasPrevPage: response.pagination.has_prev_page,
          });
        } else {
          setPagination(null);
        }
      } else {
        setTabs([]);
        setPagination(null);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch tabs:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch tabs');
      setTabs([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  };

  const deleteTab = async (tabId: number): Promise<boolean> => {
    try {
      await apiFetch({
        path: `/wp/v2/redfoxwp_tab/${tabId}`,
        method: 'DELETE',
      });

      // Remove the deleted tab from local state
      setTabs((prevTabs) => prevTabs.filter((tab) => tab.id !== tabId));
      return true;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to delete tab:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete tab');
      return false;
    }
  };

  /**
   * Navigate to a specific page
   *
   * @since 1.0.0
   *
   * @param page Page number to navigate to.
   */
  const goToPage = (page: number): void => {
    if (page >= 1 && (!pagination || page <= pagination.totalPages)) {
      setCurrentPage(page);
    }
  };

  /**
   * Change items per page
   *
   * @since 1.0.0
   *
   * @param itemsPerPage Number of items per page.
   */
  const changeItemsPerPage = (itemsPerPage: number): void => {
    setCurrentPostsPerPage(itemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  useEffect(() => {
    fetchTabs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, currentPostsPerPage, currentPage, orderBy, order, includeContent, allStatuses]);

  return {
    tabs,
    loading,
    error,
    pagination,
    refetch: fetchTabs,
    deleteTab,
    goToPage,
    changeItemsPerPage,
  };
};
